/**
 * Service for managing billing and subscription data
 */

import { buildApiUrl } from '../utils/apiConfig';
import { PaymentMethodDetails } from '../utils/subscriptionApi';
import { apiClient } from "../utils/apiClient";

export interface BillingSubscription {
  id: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'inactive';
  planTier?: string;
  planName?: string;
  planId?: string;
  currentTierLevel?: number | null;
  currentMaxAthletes?: number | null;
  maxAthletes?: number | null;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  trialEnd?: string | null;
  amount?: number;
  currency?: string;
  interval?: 'month' | 'year' | 'day';
  intervalCount?: number | null;
  paymentMethodSummary?: string;
  paymentMethod?: PaymentMethodDetails | null;
  gracePeriodEnd?: string | null;
  organizationDisplayName?: string | null;
  planCategory?: 'family' | 'organization' | string | null;
  displayMetadata?: Record<string, string> | null;
}

/**
 * Fetches billing subscription data for administrators
 * Requires ADMINISTRATOR role
 *
 * @returns Promise<BillingSubscription> - Billing subscription data
 */
export const fetchBillingSubscriptionApi = async (): Promise<BillingSubscription> => {

  const url = buildApiUrl('api/billing/subscription');

  const response = await apiClient(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include' // Send cookies automatically
  });

  if (response.status === 403) {
    throw new Error('You do not have permission to access billing information.');
  }

  if (response.status === 404) {
    throw new Error('No billing subscription found.');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch billing subscription: ${errorText || response.statusText}`);
  }

  const data = await response.json();

  // Transform backend response to match frontend interface
  // Backend returns "billingInterval" with values "monthly"/"yearly"/"annual"
  // Frontend expects "interval" with values "month"/"year"
  if (data.billingInterval) {
    data.interval = data.billingInterval === 'monthly' ? 'month' :
                    data.billingInterval === 'yearly' || data.billingInterval === 'annual' ? 'year' :
                    data.billingInterval === 'daily' ? 'day' :
                    data.billingInterval;
  }

  return data;
};

